/**
 * Permisos del FRC-017: faltar un día, salir antes o llegar tarde.
 *
 * NO son vacaciones. Un permiso es de un solo día, no descuenta saldo, y
 * puede ir con o sin goce de sueldo. Vive en su propia tabla y en su
 * propia pestaña.
 *
 * La fecha viaja como texto ISO (yyyy-MM-dd) y la hora como "hh:mm", que
 * es el formato nativo de los inputs y evita que la zona horaria mueva
 * el día al serializar.
 */

import { FrcFirmado } from "../frc/frc-firmado.model";

/**
 * Las tres casillas del formato. Son EXCLUYENTES: el FRC-017 tiene tres
 * opciones y sólo se marca una, así que esto es un tipo y no tres
 * banderas.
 */
export type TipoPermiso = "FALTA" | "SALIDA" | "ENTRADA";

/** Con o sin goce de sueldo. También excluyentes en el formato. */
export type GocePermiso = "CON" | "SIN";

/**
 * Lo que se escoge al autorizar, antes de firmar. Vive aquí porque se
 * pregunta igual desde la bandeja del líder y desde el calendario: si
 * cada pantalla tuviera su copia, un día dirían cosas distintas.
 */
export const GOCES: Array<{ valor: GocePermiso; texto: string }> = [
  { valor: "CON", texto: "Con goce de sueldo" },
  { valor: "SIN", texto: "Sin goce de sueldo" },
];

export interface Permiso {
  idPermiso?: number;
  idUsuario?: number;
  /** Resuelto por el backend, no se captura. */
  colaborador?: string;
  /** Su puesto en el organigrama. El FRC-017 no lo imprime: bajo la
   *  firma del recurso va sólo el nombre ("Nombre Recurso"). */
  puesto?: string;

  tipo?: TipoPermiso;
  /** El día del permiso. Los tres tipos lo llevan. */
  fecha?: string;
  /** "hh:mm". Sólo SALIDA y ENTRADA; en FALTA va vacío. */
  hora?: string;

  /**
   * Lo decide QUIEN AUTORIZA, al resolver. Va vacío mientras el permiso
   * está pendiente: no es algo que el colaborador se conceda.
   */
  goce?: GocePermiso;
  observaciones?: string;

  /**
   * El encabezado del formato, congelado al pedirlo. La pantalla lo
   * propone desde el organigrama y se puede corregir: no toda rama tiene
   * los tres niveles.
   */
  area?: string;
  directorArea?: string;
  gerenteOperativo?: string;
  jefeDirecto?: string;

  /**
   * PENDIENTE   pedido, esperando respuesta
   * AUTORIZADO  concedido; es el único con FRC-017 sellado
   * RECHAZADO   no se concedió, con su motivo
   * CANCELADO   el colaborador lo retiró
   */
  estatus?: string;
  idAutoriza?: number;
  autorizaNombre?: string;
  /** El formato pide "Nombre y Puesto quien Autoriza". */
  autorizaPuesto?: string;
  fechaResolucion?: string;
  motivo?: string;

  /** Firma del colaborador al pedir (PNG base64), o FISICA. */
  firmaSolicita?: string;
  /** Firma de quien autoriza. Sella el FRC-017. */
  firmaAutoriza?: string;

  idUsuarioAlta?: number;
  fechaCreacion?: string;

  /** Si firmó al pedir. El trazo no viaja en la lista: pesa. */
  tieneFirma?: boolean;
  /**
   * Alguna de las dos firmas se hizo en papel («Descargar para firmar»):
   * hay un FRC-017 firmado a mano que subir.
   */
  enPapel?: boolean;
  /** El FRC-017 firmado a mano que ya se subió (el vigente), o nada. */
  firmado?: FrcFirmado | null;

  /**
   * Sólo en la bandeja de un SUPLENTE: el autorizador de siempre, que
   * hoy no está, y por qué ("de vacaciones hasta el 20/09").
   */
  suplenteDe?: string;
  ausencia?: string;
  /** Sólo en los propios pendientes: quién tiene que contestar hoy. */
  esperaA?: string;
  /** Sólo en el calendario: es de quien lo mira, no se le ofrece resolverlo. */
  esMio?: boolean;
  /** Sólo en los propios: lo archivó (14/10/2026); sale sólo en «Archivados». */
  archivado?: boolean;
  /**
   * Sólo en la bandeja de QUIEN ADMINISTRA el calendario: le llega
   * porque el colaborador no tiene autorizador (o se tiene a sí mismo),
   * no porque sea su gente.
   */
  sinAutorizador?: boolean;
}

export interface PermisoReq {
  permiso?: Permiso;
  idPermiso?: number;
  /** Al resolver: true autoriza, false rechaza. */
  aprobar?: boolean;
  /** Motivo del rechazo. */
  motivo?: string;
  /** Con o sin goce. Sólo al autorizar: lo decide quien resuelve. */
  goce?: GocePermiso;
  /** Firma de quien resuelve (PNG base64), o FISICA. */
  firma?: string;
  desde?: string;
  hasta?: string;
  /** Bitácora: todos los estatus del rango, no sólo los que se pintan. */
  todo?: boolean;
  /** Al archivar: false lo saca del archivo; sin decir, lo archiva. */
  archivar?: boolean;
  /** Al ver el FRC-017 firmado: uno del historial; sin decir, el que se presenta. */
  idFirmado?: number;
}

export interface PermisoRes {
  permisos?: Permiso[];
  /** Los que esperan respuesta y este usuario puede resolver. */
  porResolver?: Permiso[];
  /** El encabezado propuesto desde el organigrama, para prellenar. */
  encabezado?: Permiso;
  /**
   * Candidatos a "Líder de Proyecto / Jefe directo". En un área puede
   * haber varios líderes: la pantalla los ofrece y el colaborador marca
   * cuál es el suyo.
   */
  jefes?: string[];
  puedeAutorizar?: boolean;
  /** Al subir el FRC-017 firmado a mano: lo que quedó guardado. */
  firmado?: FrcFirmado;
  /** El historial del FRC-017 firmado, del último al primero (14/10/2026). */
  firmados?: FrcFirmado[];
  idGenerado?: number;
  codigoError?: string;
  mensajeError?: string;
}
